import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { MoveDetailsSection } from '../../components/admin/MoveDetailsSection';
import { EmailActivity } from '../../components/admin/email/EmailActivity';
import { MoveDeadlineBadge, MoveDeadlineBanner, sortByMoveUrgency } from '../../components/admin/MoveDeadline';
import {
  ArrowLeft, Loader2, Search, Inbox, Phone, Mail, MessageCircle,
  Trash2, ExternalLink, Calendar, Home, Building2, RefreshCw, UserPlus, UserCheck,
  BarChart3, Tag
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import UserAvatar from '../../components/UserAvatar';
import RecordActions from '../../components/RecordActions';
import SpaceIntakeSection from '../../components/admin/SpaceIntakeSection';
import { AddLeadDialog } from '../../components/admin/AddLeadDialog';
import { ClipboardList } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOVE_DIR = { move_in: 'Move-In', move_out: 'Move-Out' };

const STATUS_CONFIG = {
  new:       { label: 'New',       color: 'bg-blue-50 text-blue-700 border-blue-200' },
  contacted: { label: 'Contacted', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  quoted:    { label: 'Quoted',    color: 'bg-purple-50 text-purple-700 border-purple-200' },
  won:       { label: 'Won',       color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  lost:      { label: 'Lost',      color: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

const STATUS_OPTIONS = ['new', 'contacted', 'quoted', 'won', 'lost'];
const FILTER_OPTIONS = ['all', ...STATUS_OPTIONS];

const LEAD_SOURCE_OPTIONS = [
  'TaskRabbit', 'Google Search', 'Google Business Profile', 'Yelp',
  'Instagram', 'Facebook', 'Nextdoor', 'Referral', 'Repeat Client',
  'Saw Our Vehicle / Sign', 'Flyer / Door Hanger', 'Other',
];

const SERVICE_LABELS = {
  standard_home: 'Standard Clean',
  move_in_move_out: 'Move-In / Move-Out',
  deep_reset: 'Deep Reset',
  airbnb: 'Airbnb Turnover',
  commercial: 'Commercial',
};

const PROP_LABELS = {
  home: 'Home',
  apartment: 'Apartment',
  airbnb: 'Airbnb',
  office: 'Office',
  retail: 'Retail',
};

const FREQ_LABELS = {
  one_time: 'One-Time',
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const ContactIcon = ({ method }) => {
  if (method === 'phone') return <Phone className="w-3.5 h-3.5" />;
  if (method === 'email') return <Mail className="w-3.5 h-3.5" />;
  return <MessageCircle className="w-3.5 h-3.5" />;
};

const STAGE_DOT = { new: '#e7924e', contacted: '#8fa0b8', quoted: '#c9a24a', won: '#7f9a5e', lost: '#b0aea4' };
const StageTab = ({ id, label, value, active, onClick }) => (
  <button role="tab" aria-selected={active} onClick={onClick} data-testid={`lead-tab-${id}`}
    className={`text-left px-5 py-5 border-r border-[#e7e4db] last:border-r-0 border-b-[3px] transition-colors ${active ? 'bg-[#eeefe5] border-b-[#f36a19]' : 'border-b-transparent hover:bg-[#f2f2e9]'}`}>
    <span className="flex items-center gap-2 text-[10px] text-[#838c74]">{STAGE_DOT[id] && <i className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: STAGE_DOT[id] }} />}{label}</span>
    <strong className="block text-[29px] font-medium text-[#3d4931] mt-1.5" data-testid={`lead-tab-count-${id}`}>{value}</strong>
  </button>
);

const QuoteRequestRow = ({ q, onOpen, onConvert, headers, onChanged }) => (
  <motion.tr
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className={`border-b border-zinc-100 cursor-pointer ${q._urgency ? 'bg-orange-50/60 hover:bg-orange-50 border-l-4 border-l-orange-500' : 'hover:bg-zinc-50'}`}
    onClick={() => onOpen(q)}
    data-testid={`row-${q.id}`}
  >
    <td className="py-3 px-4">
      <div className="flex items-center gap-3">
        <UserAvatar user={{ full_name: q.full_name, email: q.email, photo_url: q.photo_url, role: 'client' }} size="sm" testId={`lead-avatar-${q.id}`} />
        <div className="min-w-0">
          <p className="font-semibold text-zinc-900">{q.full_name}</p>
          <p className="text-xs text-zinc-500">{q.email}</p>
          <MoveDeadlineBadge q={q} />
        </div>
      </div>
    </td>
    <td className="py-3 px-4 text-sm text-zinc-700">{q.phone}</td>
    <td className="py-3 px-4 text-sm text-zinc-700">
      {SERVICE_LABELS[q.service_needed] || q.service_needed || '—'}{MOVE_DIR[q.move_direction] ? ` · ${MOVE_DIR[q.move_direction]}` : ''}
    </td>
    <td className="py-3 px-4 text-sm text-zinc-700">
      {PROP_LABELS[q.property_type] || q.property_type || '—'}
    </td>
    <td className="py-3 px-4 text-sm text-zinc-700">
      {FREQ_LABELS[q.cleaning_frequency] || q.cleaning_frequency}
    </td>
    <td className="py-3 px-4 text-sm text-zinc-700">
      {q.lead_source ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 text-xs font-medium">
          <Tag className="w-3 h-3" />{q.lead_source}
        </span>
      ) : (
        <span className="text-zinc-400 text-xs">—</span>
      )}
    </td>
    <td className="py-3 px-4">
      <Badge variant="outline" className={STATUS_CONFIG[q.status]?.color || ''}>
        {STATUS_CONFIG[q.status]?.label || q.status}
      </Badge>
    </td>
    <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(q.created_at)}</td>
    <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-end gap-1">
        {q.converted_to_customer_user_id ? (
          <Button size="sm" variant="ghost" className="text-xs text-green-700 hover:text-green-800"
            onClick={() => window.location.assign(`/admin/users/${q.converted_to_customer_user_id}`)}
            data-testid={`view-customer-${q.id}`}>
            <UserCheck className="h-3.5 w-3.5 mr-1" /> View Customer
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="text-xs rounded-full"
            onClick={() => onConvert(q)}
            data-testid={`convert-btn-${q.id}`}>
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Convert to Customer
          </Button>
        )}
        <RecordActions
          canEdit={true}
          canArchive={false}
          canDelete={true}
          deleteUrl={`/api/admin/quote-requests/${q.id}`}
          deleteConfirmTitle="Delete this quote request?"
          deleteConfirmDescription="Permanently removes this lead. This cannot be undone. Consider marking as 'Lost' instead if you want to keep it for future outreach."
          onEdit={() => onOpen(q)}
          onDeleted={onChanged}
          headers={headers}
          testIdPrefix="quote"
          entityId={q.id}
        />
      </div>
    </td>
  </motion.tr>
);

const DetailDialog = ({ open, onOpenChange, quote, onSave, onDelete, saving, headers, autoOpenIntakeId }) => {
  const [status, setStatus] = useState(quote?.status || 'new');
  const [notes, setNotes] = useState(quote?.admin_notes || '');
  const [leadSource, setLeadSource] = useState(quote?.lead_source || '');
  const [referralName, setReferralName] = useState(quote?.referral_name || '');
  const [otherSourceDetails, setOtherSourceDetails] = useState(quote?.other_source_details || '');

  useEffect(() => {
    setStatus(quote?.status || 'new');
    setNotes(quote?.admin_notes || '');
    setLeadSource(quote?.lead_source || '');
    setReferralName(quote?.referral_name || '');
    setOtherSourceDetails(quote?.other_source_details || '');
  }, [quote]);

  if (!quote) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">{quote.full_name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Email</p>
              <a href={`mailto:${quote.email}`} className="text-sm text-orange-600 hover:underline flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> {quote.email}
              </a>
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Phone</p>
              <a href={`tel:${quote.phone}`} className="text-sm text-orange-600 hover:underline flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> {quote.phone}
              </a>
            </div>
            {quote.preferred_contact && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Preferred Contact</p>
              <p className="text-sm text-zinc-900 flex items-center gap-1.5 capitalize">
                <ContactIcon method={quote.preferred_contact} /> {quote.preferred_contact}
              </p>
            </div>
            )}
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Submitted</p>
              <p className="text-sm text-zinc-900">{formatDate(quote.created_at)}</p>
            </div>
          </div>

          <div className="h-px bg-zinc-200" />

          {/* Service Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Service</p>
              <p className="text-sm text-zinc-900 flex items-center gap-1.5">
                <Home className="w-3.5 h-3.5 text-orange-500" /> <span data-testid="quote-service-label">{SERVICE_LABELS[quote.service_needed] || quote.service_needed || '—'}{MOVE_DIR[quote.move_direction] ? ` · ${MOVE_DIR[quote.move_direction]}` : ''}</span>
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Property Type</p>
              <p className="text-sm text-zinc-900 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-orange-500" /> {PROP_LABELS[quote.property_type] || quote.property_type || '—'}
              </p>
            </div>
            {quote.cleaning_frequency && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Frequency</p>
              <p className="text-sm text-zinc-900">{FREQ_LABELS[quote.cleaning_frequency] || quote.cleaning_frequency}</p>
            </div>
            )}
            {quote.preferred_date && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Preferred Date</p>
              <p className="text-sm text-zinc-900 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-orange-500" /> {quote.preferred_date}
              </p>
            </div>
            )}
          </div>

          <MoveDetailsSection quote={quote} headers={headers} />

          {quote.additional_details && !quote.move_details && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Additional Details</p>
              <p className="text-sm text-zinc-700 bg-zinc-50 rounded-lg p-3 whitespace-pre-wrap">{quote.additional_details}</p>
            </div>
          )}

          <div className="h-px bg-zinc-200" />

          {quote.attribution && (
            <div data-testid="quote-attribution">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Website attribution (automatic)</p>
              <p className="text-xs text-zinc-700 break-all">{Object.entries(quote.attribution).filter(([k]) => k !== 'captured_at').map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ')}</p>
            </div>
          )}

          {/* Lead Source Attribution */}
          <div className="bg-orange-50/50 border border-orange-100 rounded-lg p-4 space-y-3" data-testid="detail-lead-source-block">
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-orange-600" />
              <p className="text-xs font-bold text-orange-900 uppercase tracking-wider">Lead Source Attribution</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-1">Lead Source</label>
                <Select value={leadSource || '__unset__'} onValueChange={(v) => setLeadSource(v === '__unset__' ? '' : v)}>
                  <SelectTrigger className="bg-white" data-testid="detail-lead-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unset__">Not specified</SelectItem>
                    {LEAD_SOURCE_OPTIONS.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {leadSource === 'Referral' && (
                <div>
                  <label className="block text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-1">Referred By</label>
                  <Input
                    value={referralName}
                    onChange={(e) => setReferralName(e.target.value)}
                    placeholder="Name of referrer"
                    className="bg-white"
                    data-testid="detail-referral-name-input"
                  />
                </div>
              )}
              {leadSource === 'Other' && (
                <div>
                  <label className="block text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-1">Other Source Details</label>
                  <Input
                    value={otherSourceDetails}
                    onChange={(e) => setOtherSourceDetails(e.target.value)}
                    placeholder="Details about the source"
                    className="bg-white"
                    data-testid="detail-other-source-input"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-zinc-200" />

          {/* Space Intake */}
          <div className="rounded-lg border border-zinc-200 p-4 space-y-3" data-testid="lead-space-intake-block">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-orange-600" />
              <p className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Space Intake</p>
            </div>
            <SpaceIntakeSection leadId={quote.id} customerUserId={quote.converted_to_customer_user_id || undefined} headers={headers} autoOpenId={autoOpenIntakeId} />
            <div className="mt-4"><EmailActivity leadId={quote.id} headers={headers} /></div>
          </div>

          <div className="h-px bg-zinc-200" />

          {/* Status + Notes */}
          <div>
            <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Internal Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add private notes for the team..."
              rows={4}
              data-testid="admin-notes-input"
            />
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onDelete(quote.id)}
              className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
              data-testid="delete-quote-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={() => onSave(quote.id, {
                  status,
                  admin_notes: notes,
                  lead_source: leadSource,
                  referral_name: leadSource === 'Referral' ? referralName : '',
                  other_source_details: leadSource === 'Other' ? otherSourceDetails : '',
                })}
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="save-quote-btn"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AdminQuoteRequestsPage = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const sortedRequests = useMemo(() => sortByMoveUrgency(requests), [requests]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, quoted: 0, won: 0, lost: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [leadSourceFilter, setLeadSourceFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sourceReportOpen, setSourceReportOpen] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const deepLinkId = searchParams.get('id');
  const deepLinkIntake = searchParams.get('intake');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Deep link (notifications): open the referenced lead once loaded
  useEffect(() => {
    if (!deepLinkId || selected) return;
    const q = requests.find(r => r.id === deepLinkId);
    if (q) { setSelected(q); setDetailOpen(true); }
  }, [deepLinkId, requests, selected]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (leadSourceFilter !== 'all') params.lead_source = leadSourceFilter;
      if (search) params.search = search;
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/quote-requests`, { headers, params }),
        axios.get(`${API_URL}/api/admin/quote-requests/stats`, { headers }),
      ]);
      setRequests(listRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter, leadSourceFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpen = (q) => {
    setSelected(q);
    setDetailOpen(true);
  };

  const handleConvert = async (q) => {
    if (!window.confirm(`Convert ${q.full_name} into a Customer?\n\nThis creates a customer profile linked to this lead and marks it as Contacted.`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/quote-requests/${q.id}/convert-to-customer`, {}, { headers });
      const { customer_user_id, existing } = res.data;
      toast.success(existing ? 'Linked to existing customer profile' : 'Customer created from lead');
      fetchData();
      // Deep-link to the newly-created / linked customer
      setTimeout(() => window.location.assign(`/admin/users/${customer_user_id}`), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert');
    }
  };

  const handleSave = async (id, payload) => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/admin/quote-requests/${id}`, payload, { headers });
      toast.success('Quote request updated');
      setDetailOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quote request? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/quote-requests/${id}`, { headers });
      toast.success('Quote request deleted');
      setDetailOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-quote-requests-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-2 min-h-16 py-2">
            <div className="flex items-center gap-3">
              <Link to="/admin/dashboard" className="text-zinc-500 hover:text-zinc-900" data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <Inbox className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-zinc-900">Quote Requests</h1>
                <p className="text-xs text-zinc-500">Manage incoming leads from the website</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => setAddLeadOpen(true)} className="bg-[#F56F05] hover:bg-[#e06300] text-white" data-testid="add-lead-btn">
                + Add lead
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSourceReportOpen(true)} data-testid="source-report-btn">
                <BarChart3 className="w-4 h-4 mr-2" /> Source Report
              </Button>
              <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div role="tablist" aria-label="Filter by lead status" className="grid grid-cols-3 md:grid-cols-6 rounded-[9px] border border-[#e7e4db] bg-[#fffefa] overflow-hidden mb-6" data-testid="lead-status-tabs">
          <StageTab id="all" label="All leads" value={stats.total} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
          {STATUS_OPTIONS.map(s => (
            <StageTab key={s} id={s} label={STATUS_CONFIG[s].label} value={stats[s] || 0} active={statusFilter === s} onClick={() => setStatusFilter(s)} />
          ))}
        </div>

        {/* Filters */}
        <Card className="border-zinc-200 mb-4">
          <CardContent className="p-4 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTER_OPTIONS.map(s => (
                  <SelectItem key={s} value={s}>
                    {s === 'all' ? 'All Statuses' : STATUS_CONFIG[s]?.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={leadSourceFilter} onValueChange={setLeadSourceFilter}>
              <SelectTrigger className="w-full md:w-56" data-testid="lead-source-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="__unspecified__">Unspecified</SelectItem>
                {LEAD_SOURCE_OPTIONS.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {!loading && <MoveDeadlineBanner count={sortedRequests.filter((q) => q._urgency).length} />}

        {/* Table */}
        <Card className="border-zinc-200 overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : requests.length === 0 ? (
            <div className="p-12 text-center" data-testid="empty-state">
              <Inbox className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-600 font-semibold">No quote requests found</p>
              <p className="text-sm text-zinc-400 mt-1">Submissions from the website will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="quotes-table">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Contact</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Phone</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Service</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Property</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Frequency</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Source</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Submitted</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRequests.map(q => (
                    <QuoteRequestRow
                      key={q.id || q.email + q.created_at}
                      q={q}
                      onOpen={handleOpen}
                      onConvert={handleConvert}
                      headers={headers}
                      onChanged={fetchData}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>

      <DetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        quote={selected}
        onSave={handleSave}
        onDelete={handleDelete}
        saving={saving}
        headers={headers}
        autoOpenIntakeId={deepLinkIntake}
      />

      <SourceReportDialog
        open={sourceReportOpen}
        onOpenChange={setSourceReportOpen}
        headers={headers}
      />
      {addLeadOpen && <AddLeadDialog headers={headers} onClose={() => setAddLeadOpen(false)} onSaved={() => { setAddLeadOpen(false); fetchData(); }} />}
    </div>
  );
};

const SourceReportDialog = ({ open, onOpenChange, headers }) => {
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API_URL}/api/admin/quote-requests/stats-by-source`, { headers })
      .then(r => setSources(r.data.sources || []))
      .catch(() => toast.error('Failed to load source report'))
      .finally(() => setLoading(false));
  }, [open, headers]);

  const totals = sources.reduce((acc, s) => ({
    leads: acc.leads + s.leads_count,
    booked: acc.booked + s.booked_count,
    revenue: acc.revenue + s.revenue_paid,
  }), { leads: 0, booked: 0, revenue: 0 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="source-report-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-600" /> Lead Source Report
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Performance breakdown of every marketing channel producing website leads.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="p-10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : sources.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">No lead data yet. Sources will appear once leads come in.</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-zinc-200 bg-white p-3">
                <p className="text-2xl font-bold text-zinc-900" data-testid="report-total-leads">{totals.leads}</p>
                <p className="text-xs text-zinc-500 uppercase mt-1 tracking-wider">Leads</p>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white p-3">
                <p className="text-2xl font-bold text-emerald-600" data-testid="report-total-booked">{totals.booked}</p>
                <p className="text-xs text-zinc-500 uppercase mt-1 tracking-wider">Booked</p>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white p-3">
                <p className="text-2xl font-bold text-orange-600" data-testid="report-total-revenue">${totals.revenue.toFixed(2)}</p>
                <p className="text-xs text-zinc-500 uppercase mt-1 tracking-wider">Revenue Paid</p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-zinc-200">
              <table className="w-full" data-testid="source-report-table">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Source</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Leads</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Booked</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Revenue</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map(s => (
                    <tr key={s.lead_source} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`source-row-${s.lead_source.replace(/\W+/g, '-').toLowerCase()}`}>
                      <td className="py-2.5 px-3 text-sm font-semibold text-zinc-900">{s.lead_source}</td>
                      <td className="py-2.5 px-3 text-sm text-right text-zinc-700">{s.leads_count}</td>
                      <td className="py-2.5 px-3 text-sm text-right text-emerald-700 font-medium">{s.booked_count}</td>
                      <td className="py-2.5 px-3 text-sm text-right text-zinc-900 font-medium">${s.revenue_paid.toFixed(2)}</td>
                      <td className="py-2.5 px-3 text-sm text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.conversion_rate >= 0.3 ? 'bg-emerald-50 text-emerald-700' : s.conversion_rate >= 0.15 ? 'bg-amber-50 text-amber-700' : 'bg-zinc-100 text-zinc-600'}`}>
                          {(s.conversion_rate * 100).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminQuoteRequestsPage;
